/**
 * Payout Store
 *
 * Vuex store per il modulo payout.
 */
import { ApiException } from "@/domain/core/exception/exceptions"
import { emptyListResponse } from "@/domain/core/utils/django"
import { SCOPE_TYPES, CERTIFICATION_STATUS } from "./constants"

const state = {
    // Discovery - lista di contract managers accessibili
    contractManagers: [],

    // Selected scope
    selectedCmId: null,
    scopeType: null, // 'contract-manager'

    // Data
    payouts: { ...emptyListResponse },
    payout: null,
    statistics: {
        total_revenue: "0.00",
        total_fees: "0.00",
        total_payouts: "0.00",
        total_refunds: "0.00",
        pending_balance: "0.00",
        currency: "EUR",
        pica_services: []
    },

    // Certification
    certification: {
        status: CERTIFICATION_STATUS.NOT_CERTIFIED,
        vat_regime: false,
        region: '',
        document: null
    },

    // Separate lists
    payoutsInProgress: { ...emptyListResponse },
    payoutsHistory: { ...emptyListResponse },

    // Loading states
    isLoadingScopes: false,
    isLoadingList: false,
    isLoadingStatistics: false,
    isLoadingCertification: false,
    isUploadingInvoice: false,
    isRequesting: false
}

export const getters = {
    // Discovery
    contractManagers: state => state.contractManagers,
    hasMultipleCMs: state => state.contractManagers.length > 1,
    hasSingleCM: state => state.contractManagers.length === 1,
    hasNoCMs: state => state.contractManagers.length === 0,

    // Selected
    selectedCmId: state => state.selectedCmId,
    scopeType: state => state.scopeType,
    selectedCm: state => state.contractManagers.find(cm => cm.id === state.selectedCmId) || null,

    // Data
    payouts: state => state.payouts,
    payout: state => state.payout,
    statistics: state => state.statistics,

    // Certification
    certification: state => state.certification,
    certificationStatus: state => state.certification.status,
    isCertified: state => state.certification.status === CERTIFICATION_STATUS.CERTIFIED,

    // Separate lists
    payoutsInProgress: state => state.payoutsInProgress,
    payoutsHistory: state => state.payoutsHistory,

    // Loading
    isLoadingScopes: state => state.isLoadingScopes,
    isLoadingList: state => state.isLoadingList,
    isLoadingStatistics: state => state.isLoadingStatistics,
    isLoadingCertification: state => state.isLoadingCertification,
    isUploadingInvoice: state => state.isUploadingInvoice,
    isRequesting: state => state.isRequesting,
    isLoading: state => state.isLoadingScopes || state.isLoadingList || state.isLoadingStatistics,

    isContractManagerScope: state => state.scopeType === SCOPE_TYPES.CONTRACT_MANAGER
}

export const mutations = {
    // Discovery
    contractManagers(state, contractManagers) {
        state.contractManagers = contractManagers
    },
    isLoadingScopes(state, value) {
        state.isLoadingScopes = value
    },

    // Selected
    selectedCmId(state, cmId) {
        state.selectedCmId = cmId
        state.scopeType = cmId ? SCOPE_TYPES.CONTRACT_MANAGER : null
    },

    // Data
    payouts(state, payouts) {
        state.payouts = payouts
    },
    payout(state, payout) {
        state.payout = payout
        // Aggiorna anche nella lista se presente
        if (payout && state.payouts.results) {
            state.payouts.results = state.payouts.results.map(p =>
                p.id === payout.id ? payout : p
            )
        }
    },
    statistics(state, statistics) {
        state.statistics = statistics
    },

    // Certification
    certification(state, data) {
        state.certification = data
    },

    // Separate lists
    payoutsInProgress(state, data) {
        state.payoutsInProgress = data
    },
    payoutsHistory(state, data) {
        state.payoutsHistory = data
    },

    // Loading
    isLoadingList(state, value) {
        state.isLoadingList = value
    },
    isLoadingStatistics(state, value) {
        state.isLoadingStatistics = value
    },
    isLoadingCertification(state, value) {
        state.isLoadingCertification = value
    },
    isUploadingInvoice(state, value) {
        state.isUploadingInvoice = value
    },
    isRequesting(state, value) {
        state.isRequesting = value
    },
    // Reset
    resetData(state) {
        state.payouts = { ...emptyListResponse }
        state.payout = null
        state.statistics = {
            total_revenue: "0.00",
            total_fees: "0.00",
            total_payouts: "0.00",
            total_refunds: "0.00",
            pending_balance: "0.00",
            currency: "EUR",
            pica_services: []
        }
        state.certification = {
            status: CERTIFICATION_STATUS.NOT_CERTIFIED,
            vat_regime: false,
            region: '',
            document: null
        }
        state.payoutsInProgress = { ...emptyListResponse }
        state.payoutsHistory = { ...emptyListResponse }
    }
}

export const actions = service => ({
    // ==================== Discovery ====================

    /**
     * Fetch contract managers accessibili e auto-seleziona il primo
     */
    async fetchScopes({ commit, dispatch }) {
        commit('isLoadingScopes', true)
        try {
            const contractManagers = await service.fetchScopes()
            commit('contractManagers', contractManagers)

            // Auto-seleziona il primo CM se disponibile
            if (contractManagers.length > 0) {
                await dispatch('selectContractManager', contractManagers[0].id)
            }

            return contractManagers
        } catch (e) {
            throw new ApiException(e)
        } finally {
            commit('isLoadingScopes', false)
        }
    },

    /**
     * Fetch contract managers per certificazione (filtrati per permesso see)
     */
    async fetchCertificationScopes({ commit }) {
        commit('isLoadingScopes', true)
        try {
            const contractManagers = await service.fetchCertificationScopes()
            commit('contractManagers', contractManagers)
            return contractManagers
        } catch (e) {
            throw new ApiException(e)
        } finally {
            commit('isLoadingScopes', false)
        }
    },

    // ==================== Selection ====================

    /**
     * Setta il CM selezionato (senza caricare dati)
     */
    setSelectedCmId({ commit }, cmId) {
        commit('selectedCmId', cmId)
    },

    /**
     * Seleziona un contract manager e carica i suoi dati
     */
    async selectContractManager({ commit, dispatch }, cmId) {
        commit('resetData')
        commit('selectedCmId', cmId)

        // Carica config permessi e dati in parallelo
        await Promise.all([
            dispatch('login/fetchConfig', { type: 'accounting_payout_contract_manager', id: cmId }, { root: true }),
            dispatch('fetchCertification'),
            dispatch('fetchStatistics'),
            dispatch('fetchPayoutsInProgress'),
            dispatch('fetchPayoutsHistory')
        ])
    },

    // ==================== Contract Manager Actions ====================

    /**
     * Fetch lista payouts per il CM selezionato (generico, con filtri)
     */
    async fetchPayoutList({ commit, getters }, { filters, pagination, sorting }) {
        if (!getters.selectedCmId) return

        commit('isLoadingList', true)
        try {
            const payouts = await service.fetchPayoutList(
                getters.selectedCmId,
                filters,
                pagination,
                sorting
            )
            commit('payouts', payouts)
            return payouts
        } catch (e) {
            throw new ApiException(e)
        } finally {
            commit('isLoadingList', false)
        }
    },

    /**
     * Fetch payouts in lavorazione (pending, invoice_uploaded)
     */
    async fetchPayoutsInProgress({ commit, getters }) {
        if (!getters.selectedCmId) return

        try {
            const payouts = await service.fetchPayoutList(
                getters.selectedCmId,
                { section: 'in_progress' },
                { page: 1, page_size: 50 },
                {}
            )
            commit('payoutsInProgress', payouts)
            return payouts
        } catch (e) {
            commit('payoutsInProgress', { ...emptyListResponse })
        }
    },

    /**
     * Fetch storico pagamenti (paid)
     */
    async fetchPayoutsHistory({ commit, getters }, pagination = { page: 1, page_size: 20 }) {
        if (!getters.selectedCmId) return

        try {
            const payouts = await service.fetchPayoutList(
                getters.selectedCmId,
                { section: 'history' },
                pagination,
                { column: 'paid_at', asc: false }
            )
            commit('payoutsHistory', payouts)
            return payouts
        } catch (e) {
            commit('payoutsHistory', { ...emptyListResponse })
        }
    },

    /**
     * Fetch dettaglio payout
     */
    async fetchPayoutDetail({ commit, getters }, payoutId) {
        if (!getters.selectedCmId) return

        try {
            const payout = await service.fetchPayoutDetail(getters.selectedCmId, payoutId)
            commit('payout', payout)
            return payout
        } catch (e) {
            throw new ApiException(e)
        }
    },

    /**
     * Fetch statistiche per il CM selezionato
     */
    async fetchStatistics({ commit, getters }) {
        if (!getters.selectedCmId) return

        commit('isLoadingStatistics', true)
        try {
            const statistics = await service.fetchStatistics(getters.selectedCmId)
            commit('statistics', statistics)
            return statistics
        } catch (e) {
            throw new ApiException(e)
        } finally {
            commit('isLoadingStatistics', false)
        }
    },

    /**
     * Fetch stato certificazione
     * Backend ritorna 404 se nessuna certificazione è stata mai inviata → status: not_certified
     */
    async fetchCertification({ commit, getters }) {
        if (!getters.selectedCmId) return

        commit('isLoadingCertification', true)
        try {
            const data = await service.fetchCertification(getters.selectedCmId)
            commit('certification', data)
            return data
        } catch (e) {
            // 404 = mai certificato → not_certified
            commit('certification', {
                status: CERTIFICATION_STATUS.NOT_CERTIFIED,
                vat_regime: false,
                region: '',
                document: null,
                rejected_reason: null
            })
        } finally {
            commit('isLoadingCertification', false)
        }
    },

    /**
     * Verifica P.IVA tramite VIES (solo EU)
     */
    async validateVat({ getters }, { vatNumber, cmId = null }) {
        const id = cmId || getters.selectedCmId
        if (!id) return
        return await service.validateVat(id, vatNumber)
    },

    /**
     * Invia certificazione azienda (POST — solo per not_certified/404)
     */
    async submitCertification({ commit, dispatch, getters }, data) {
        if (!getters.selectedCmId) return

        commit('isLoadingCertification', true)
        try {
            const result = await service.submitCertification(getters.selectedCmId, data)
            commit('certification', result)
            return result
        } catch (e) {
            throw new ApiException(e)
        } finally {
            commit('isLoadingCertification', false)
        }
    },

    /**
     * Aggiorna certificazione azienda (PATCH — per stati pending/rejected)
     */
    async updateCertification({ commit, getters }, data) {
        if (!getters.selectedCmId) return

        commit('isLoadingCertification', true)
        try {
            const result = await service.updateCertification(getters.selectedCmId, data)
            commit('certification', result)
            return result
        } catch (e) {
            throw new ApiException(e)
        } finally {
            commit('isLoadingCertification', false)
        }
    },

    /**
     * Upload fattura per un payout
     */
    async uploadInvoice({ commit, dispatch, getters }, { payoutId, invoice }) {
        if (!getters.selectedCmId) return

        commit('isUploadingInvoice', true)
        try {
            const payout = await service.uploadInvoice(getters.selectedCmId, payoutId, invoice)
            commit('payout', payout)

            // Refresh lista in lavorazione
            await dispatch('fetchPayoutsInProgress')

            return payout
        } catch (e) {
            throw new ApiException(e)
        } finally {
            commit('isUploadingInvoice', false)
        }
    },

    /**
     * Download excel statistiche mese corrente
     */
    async downloadStatistics({ getters }) {
        if (!getters.selectedCmId) return

        try {
            await service.downloadStatistics(getters.selectedCmId)
        } catch (e) {
            throw new ApiException(e)
        }
    },

    /**
     * Download excel payout
     */
    async downloadPayout({ getters }, payoutId) {
        if (!getters.selectedCmId) return

        try {
            await service.downloadPayout(getters.selectedCmId, payoutId)
        } catch (e) {
            throw new ApiException(e)
        }
    },

})

export default payoutService => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(payoutService)
})
